/**
 * 세션 관리 서비스
 * 단일 세션 정책: 한 계정에 하나의 브라우저만 로그인 가능
 */

import { doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const SESSION_STORAGE_KEY = 'ddalkkak_session_id';

/**
 * 브라우저 + OS 정보 가져오기
 */
export const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;

  // 브라우저 감지
  let browser = 'Unknown';
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Edg')) {
    browser = 'Edge';
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    browser = 'Internet Explorer';
  }

  // OS 감지
  let os = 'Unknown';
  if (userAgent.includes('Windows')) {
    os = 'Windows';
  } else if (userAgent.includes('Mac')) {
    os = 'macOS';
  } else if (userAgent.includes('Linux')) {
    os = 'Linux';
  } else if (userAgent.includes('Android')) {
    os = 'Android';
  } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    os = 'iOS';
  }

  return `${browser}/${os}`;
};

/**
 * 세션 ID 가져오기 (없으면 생성)
 */
export const getOrCreateSessionId = () => {
  let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);

  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }

  return sessionId;
};

/**
 * 현재 세션 ID 가져오기
 */
export const getCurrentSessionId = () => {
  return localStorage.getItem(SESSION_STORAGE_KEY);
};

/**
 * 기존 활성 세션 확인
 * @param {string} userId
 * @returns {Promise<Object|null>} 기존 세션 정보 또는 null
 */
export const checkExistingSession = async (userId) => {
  try {
    const userDocRef = doc(db, 'USERS', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();
    const activeSession = userData.activeSession;

    if (!activeSession || !activeSession.sessionId) {
      return null;
    }

    const currentSessionId = getOrCreateSessionId();

    // 같은 세션이면 충돌 없음
    if (activeSession.sessionId === currentSessionId) {
      return null;
    }

    // 다른 세션이 활성화되어 있음
    return activeSession;
  } catch (error) {
    console.error('세션 확인 오류:', error);
    return null;
  }
};

/**
 * 새 세션 등록 (기존 세션 덮어쓰기)
 * @param {string} userId
 */
export const registerSession = async (userId) => {
  try {
    const sessionId = getOrCreateSessionId();
    const deviceInfo = getDeviceInfo();

    const userDocRef = doc(db, 'USERS', userId);
    await updateDoc(userDocRef, {
      activeSession: {
        sessionId,
        deviceInfo,
        loginAt: new Date()
      }
    });

    console.log('✅ 세션 등록 완료:', sessionId, deviceInfo);
    return true;
  } catch (error) {
    console.error('세션 등록 오류:', error);
    return false;
  }
};

/**
 * 세션 삭제 (로그아웃 시)
 * @param {string} userId
 */
export const clearSession = async (userId) => {
  try {
    const userDocRef = doc(db, 'USERS', userId);
    await updateDoc(userDocRef, {
      activeSession: null
    });

    console.log('✅ 세션 삭제 완료');
    return true;
  } catch (error) {
    console.error('세션 삭제 오류:', error);
    return false;
  }
};

/**
 * 세션 변경 감지 리스너
 * @param {string} userId
 * @param {function} onSessionInvalid - 세션 무효화 시 콜백
 * @returns {function} unsubscribe 함수
 */
export const subscribeToSessionChanges = (userId, onSessionInvalid) => {
  const userDocRef = doc(db, 'USERS', userId);
  const currentSessionId = getCurrentSessionId();

  const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
    if (!docSnapshot.exists()) return;

    const userData = docSnapshot.data();
    const activeSession = userData.activeSession;

    // 세션이 없거나 현재 세션과 다르면 로그아웃
    if (activeSession && activeSession.sessionId !== currentSessionId) {
      console.log('⚠️ 다른 기기에서 로그인 감지, 로그아웃 처리');
      onSessionInvalid(activeSession.deviceInfo);
    }
  });

  return unsubscribe;
};
